import { useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useForm } from "@tanstack/react-form";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createTableHook, tableFeatures } from "@tanstack/react-table";
import { Button } from "@astryxdesign/core/Button";
import { TextInput } from "@astryxdesign/core/TextInput";
import * as stylex from "@stylexjs/stylex";
import { errorMessage, queries } from "../lib/api";
import {
  cloudAPI,
  cloudQuery,
  cloudUsersQuery,
  connectionFormSchema,
  onboardingFormSchema,
  type CloudState,
  type CloudNode,
  type CloudConnection,
  type ConnectionForm,
  type OnboardingForm,
} from "../lib/cloud";
import { styles } from "../lib/styles";

export const Route = createFileRoute("/cloud")({ component: CloudPage });
const date = (value: string | null) => (value ? new Date(value).toLocaleString() : "Never");
const label = (value: string) => value.replaceAll("_", " ");
const { useAppTable, createAppColumnHelper } = createTableHook({ features: tableFeatures({}) });
const helper = createAppColumnHelper<CloudNode>();

function CloudPage() {
  const me = useQuery(queries.me());
  const cloud = useQuery({ ...cloudQuery(), enabled: me.data?.admin === true });
  const [selected, setSelected] = useState<CloudNode | null>(null);
  const [connection, setConnection] = useState<CloudConnection | null>(null);
  if (me.isPending) return <p aria-busy="true">Checking cloud permissions…</p>;
  if (!me.data?.admin)
    return (
      <section {...stylex.props(styles.panel)}>
        <h1 {...stylex.props(styles.title)}>Cloud nodes</h1>
        <p>Cloud management requires administrator access.</p>
      </section>
    );
  return (
    <>
      <div {...stylex.props(styles.pageHeading)}>
        <div>
          <h1 {...stylex.props(styles.title)}>Cloud nodes</h1>
          <p {...stylex.props(styles.description)}>
            Discover E2E virtual machines, onboard selected nodes into NetBird, and manage their SSH
            access.
          </p>
        </div>
      </div>
      {cloud.error && (
        <p role="alert" {...stylex.props(styles.error)}>
          {errorMessage(cloud.error)}
        </p>
      )}
      {cloud.isPending && <p aria-busy="true">Loading cloud inventory…</p>}
      {cloud.data && (
        <>
          {!cloud.data.configured && (
            <div {...stylex.props(styles.notice)}>
              <p>
                Cloud integration is not configured on this server. Configure the service before
                adding credentials.
              </p>
              <details>
                <summary>Operator setup</summary>
                <p>
                  CLOUD_OWNER_SUBJECT is mandatory: set it to your stable SSO subject. Set
                  CLOUD_KEY_FILE to a private encryption-key file. For onboarding, configure
                  CLOUD_WORKER_CIDR, CLOUD_NETBIRD_MANAGEMENT_URL and the existing NetBird,
                  provisioning-key, gateway and SSH CA settings.
                </p>
                <p>
                  Provide a verified NetBird artifact using CLOUD_NETBIRD_ARTIFACT_FILE,
                  CLOUD_NETBIRD_ARTIFACT_SHA256, CLOUD_NETBIRD_ARTIFACT_VERSION and
                  CLOUD_NETBIRD_ARTIFACT_ARCH. Each worker configuration supplies one
                  architecture-specific package; its architecture must match the target host. Enable
                  CLOUD_FIREWALL_PILOT_VALIDATED only after the temporary security-group behavior
                  has passed the operator pilot. Keep all secret values outside the UI and
                  repository.
                </p>
              </details>
            </div>
          )}
          <p {...stylex.props(styles.notice)}>
            Onboarding starts only when you select a node. Your configured owner identity receives
            standing access after verification; other users and groups need an explicit access rule.
            Existing NetBird installations are adopted when compatible, otherwise preserved as
            unmanaged. Automated host setup currently supports validated Ubuntu/Debian hosts with
            systemd. Windows and other OS remain visible and unmanaged until their SSH adapters are
            validated. RDP and Docker automation are not available in this release.
          </p>
          <Connections state={cloud.data} onEdit={setConnection} />
          <ConnectionEditor
            key={connection?.id ?? "new"}
            connection={connection}
            configured={cloud.data.configured}
            onDone={() => setConnection(null)}
          />
          <Inventory
            state={cloud.data}
            onSelect={setSelected}
            owner={me.data.subject === cloud.data.owner_subject}
          />
          {selected && (
            <Onboarding
              key={selected.id}
              node={selected}
              connection={cloud.data.connections.find((item) => item.id === selected.connection_id)}
              onClose={() => setSelected(null)}
            />
          )}
          {cloud.data.configured && me.data.subject === cloud.data.owner_subject && (
            <UserAccess state={cloud.data} />
          )}
          <Jobs state={cloud.data} />
        </>
      )}
    </>
  );
}

function Connections({
  state,
  onEdit,
}: {
  state: CloudState;
  onEdit: (value: CloudConnection) => void;
}) {
  const cache = useQueryClient();
  const sync = useMutation({
    mutationFn: cloudAPI.sync,
    onSuccess: () => cache.invalidateQueries({ queryKey: ["cloud"] }),
  });
  return (
    <section {...stylex.props(styles.panel)}>
      <h2 {...stylex.props(styles.panelHeading)}>E2E connections</h2>
      {state.connections.length === 0 && (
        <p {...stylex.props(styles.empty)}>Add a connection to discover your VMs.</p>
      )}
      {sync.error && (
        <p role="alert" {...stylex.props(styles.error)}>
          {errorMessage(sync.error)}
        </p>
      )}
      {sync.isSuccess && <output>Inventory synchronization queued.</output>}
      {state.connections.map((item) => (
        <div key={item.id} {...stylex.props(styles.pageHeading)}>
          <div>
            <strong>{item.name}</strong>
            <p {...stylex.props(styles.small)}>
              Project {item.project_id} · {item.location} · {item.enabled ? "Enabled" : "Disabled"}{" "}
              · Credentials {item.credential_set ? "configured" : "missing"}
            </p>
            <p {...stylex.props(styles.small)}>Last synchronized: {date(item.last_sync)}</p>
            {item.last_error && (
              <p role="alert" {...stylex.props(styles.error)}>
                {item.last_error}
              </p>
            )}
          </div>
          <div {...stylex.props(styles.nav)}>
            <Button label="Edit connection" size="sm" onClick={() => onEdit(item)} />
            <Button
              label="Synchronize inventory"
              size="sm"
              isDisabled={!item.enabled || !item.credential_set}
              isLoading={sync.isPending && sync.variables === item.id}
              onClick={() => sync.mutate(item.id)}
            />
          </div>
        </div>
      ))}
    </section>
  );
}

function ConnectionEditor({
  connection,
  configured,
  onDone,
}: {
  connection: CloudConnection | null;
  configured: boolean;
  onDone: () => void;
}) {
  const cache = useQueryClient();
  const [credentialError, setCredentialError] = useState("");
  const save = useMutation({
    mutationFn: cloudAPI.saveConnection,
    onSuccess: () => cache.invalidateQueries({ queryKey: ["cloud"] }),
  });
  const defaults: ConnectionForm = {
    id: connection?.id ?? "",
    name: connection?.name ?? "",
    project_id: connection ? String(connection.project_id) : "",
    location: connection?.location === "Chennai" ? "Chennai" : "Delhi",
    enabled: connection?.enabled ?? true,
    network_changes: connection?.network_changes ?? true,
    api_key: "",
    api_token: "",
  };
  const form = useForm({
    defaultValues: defaults,
    validators: { onSubmit: connectionFormSchema },
    onSubmit: async ({ value }) => {
      if (
        (!connection && (!value.api_key || !value.api_token)) ||
        Boolean(value.api_key) !== Boolean(value.api_token)
      ) {
        setCredentialError("Provide both the API key and bearer token together.");
        return;
      }
      setCredentialError("");
      await save.mutateAsync(value);
      form.reset({ ...value, api_key: "", api_token: "" });
      onDone();
    },
  });
  return (
    <section {...stylex.props(styles.panel)}>
      <h2 {...stylex.props(styles.panelHeading)}>
        {connection ? "Edit E2E connection" : "Add E2E connection"}
      </h2>
      <p {...stylex.props(styles.small)}>
        Credentials stay on the server. Leave both credential fields empty when editing to retain
        them. Scope the E2E account credentials to the selected project and required capabilities.
      </p>
      <form
        {...stylex.props(styles.formContainer)}
        onSubmit={(event) => {
          event.preventDefault();
          void form.handleSubmit();
        }}
      >
        <div {...stylex.props(styles.form)}>
          {(
            [
              ["name", "Connection name"],
              ["project_id", "E2E project ID"],
              ["api_key", "API key"],
              ["api_token", "Bearer token"],
            ] as const
          ).map(([name, title]) => (
            <form.Field key={name} name={name}>
              {(field) => (
                <TextInput
                  label={title}
                  type={name.startsWith("api_") ? "password" : "text"}
                  value={field.state.value}
                  onChange={field.handleChange}
                  isRequired={!connection || !name.startsWith("api_")}
                  status={
                    field.state.meta.errors.length
                      ? { type: "error", message: "Check this value" }
                      : undefined
                  }
                />
              )}
            </form.Field>
          ))}
          <form.Field name="location">
            {(field) => (
              <label {...stylex.props(styles.field)}>
                Location
                <select
                  {...stylex.props(styles.select)}
                  value={field.state.value}
                  onChange={(event) =>
                    field.handleChange(event.target.value === "Chennai" ? "Chennai" : "Delhi")
                  }
                >
                  <option>Delhi</option>
                  <option>Chennai</option>
                </select>
              </label>
            )}
          </form.Field>
          <form.Field name="enabled">
            {(field) => (
              <label>
                <input
                  type="checkbox"
                  checked={field.state.value}
                  onChange={(event) => field.handleChange(event.target.checked)}
                />{" "}
                Enable this connection
              </label>
            )}
          </form.Field>
          <form.Field name="network_changes">
            {(field) => (
              <label {...stylex.props(styles.full)}>
                <input
                  type="checkbox"
                  checked={field.state.value}
                  onChange={(event) => field.handleChange(event.target.checked)}
                />{" "}
                Allow temporary SSH network access restricted to the provisioning worker IP
              </label>
            )}
          </form.Field>
        </div>
        <form.Subscribe selector={(state) => state.errors.length > 0}>
          {(invalid) =>
            invalid && <p role="alert">Check the connection name, project ID and credentials.</p>
          }
        </form.Subscribe>
        {(credentialError || save.error) && (
          <p role="alert" {...stylex.props(styles.error)}>
            {credentialError || errorMessage(save.error)}
          </p>
        )}
        {save.isSuccess && (
          <output>Connection saved. Synchronize inventory to discover nodes.</output>
        )}
        <div {...stylex.props(styles.actions)}>
          <Button
            label="Save connection"
            type="submit"
            isDisabled={!configured}
            isLoading={save.isPending}
          />
          {connection && <Button label="Cancel editing" onClick={onDone} />}
        </div>
      </form>
    </section>
  );
}

function Inventory({
  state,
  onSelect,
  owner,
}: {
  state: CloudState;
  onSelect: (value: CloudNode) => void;
  owner: boolean;
}) {
  const [search, setSearch] = useState("");
  const cache = useQueryClient();
  const changed = async () => {
    await cache.invalidateQueries({ queryKey: ["cloud"] });
    await cache.invalidateQueries({ queryKey: ["access"] });
  };
  const offboard = useMutation({ mutationFn: cloudAPI.offboard, onSuccess: changed });
  const enable = useMutation({ mutationFn: cloudAPI.enable, onSuccess: changed });
  const columns = useMemo(
    () => [
      helper.display({
        id: "node",
        header: "Virtual machine",
        cell: ({ row }) => (
          <>
            <strong>{row.original.name}</strong>
            <p {...stylex.props(styles.small)}>
              {row.original.provider_id} ·{" "}
              {state.connections.find((item) => item.id === row.original.connection_id)?.name}
            </p>
          </>
        ),
      }),
      helper.display({
        id: "os",
        header: "OS / power",
        cell: ({ row }) => (
          <>
            {row.original.os || "Unknown OS"}
            <p {...stylex.props(styles.small)}>
              {row.original.missing ? "Missing from inventory" : row.original.status}
            </p>
          </>
        ),
      }),
      helper.display({
        id: "address",
        header: "Addresses",
        cell: ({ row }) => (
          <>
            {row.original.public_ip || "No public IP"}
            <p {...stylex.props(styles.small)}>{row.original.private_ip}</p>
          </>
        ),
      }),
      helper.display({
        id: "management",
        header: "Management",
        cell: ({ row }) => (
          <>
            <span {...stylex.props(styles.status)}>
              {label(row.original.management_status || "discovered")}
            </span>
            <p {...stylex.props(styles.small)}>Seen {date(row.original.last_seen)}</p>
          </>
        ),
      }),
      helper.display({
        id: "actions",
        header: "Manage",
        cell: ({ row }) => {
          const node = row.original;
          const busy = state.jobs.some(
            (job) =>
              job.node_id === node.id &&
              ["queued", "working", "cleanup_required"].includes(job.status),
          );
          const connection = state.connections.find((item) => item.id === node.connection_id);
          if (node.server_id && node.management_status === "disabled") {
            return owner ? (
              <Button
                label="Enable managed access"
                size="sm"
                isDisabled={busy || node.missing || !connection?.enabled || !state.configured}
                isLoading={enable.isPending && enable.variables === node.id}
                onClick={() => enable.mutate(node.id)}
              />
            ) : (
              <span {...stylex.props(styles.small)}>Owner can enable access</span>
            );
          }
          return node.server_id ? (
            <Button
              label="Disable managed access"
              size="sm"
              isLoading={offboard.isPending && offboard.variables === node.id}
              onClick={() => offboard.mutate(node.id)}
            />
          ) : (
            <Button
              label="Review onboarding"
              size="sm"
              isDisabled={node.missing || busy || !connection?.enabled || !state.configured}
              onClick={() => onSelect(node)}
            />
          );
        },
      }),
    ],
    [state, onSelect, offboard, enable, owner],
  );
  const needle = search.trim().toLowerCase();
  const data = state.nodes.filter((node) =>
    [
      node.name,
      node.provider_id,
      node.os,
      node.public_ip,
      node.private_ip,
      node.management_status,
    ].some((value) => value.toLowerCase().includes(needle)),
  );
  const table = useAppTable({ columns, data });
  return (
    <section {...stylex.props(styles.panel)}>
      <h2 {...stylex.props(styles.panelHeading)}>Discovered nodes</h2>
      <TextInput
        label="Find a node"
        value={search}
        onChange={setSearch}
        placeholder="Name, OS, address or status"
      />
      {(offboard.error || enable.error) && (
        <p role="alert" {...stylex.props(styles.error)}>
          {errorMessage(offboard.error || enable.error)}
        </p>
      )}
      {enable.isSuccess && (
        <output>
          Managed access enable requested. Access synchronization verifies the server before
          allowing connections.
        </output>
      )}
      {offboard.isSuccess && (
        <output>Managed access disable requested. Existing recovery access is preserved.</output>
      )}
      {data.length === 0 ? (
        <p {...stylex.props(styles.empty)}>
          {state.nodes.length
            ? "No matching nodes."
            : "No nodes discovered. Synchronize an enabled E2E connection."}
        </p>
      ) : (
        <div {...stylex.props(styles.tableWrap)}>
          <table {...stylex.props(styles.table)}>
            <thead>
              {table.getHeaderGroups().map((group) => (
                <tr key={group.id}>
                  {group.headers.map((header) => (
                    <th key={header.id} {...stylex.props(styles.th)}>
                      <table.FlexRender header={header} />
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {table.getRowModel().rows.map((row) => (
                <tr key={row.id}>
                  {row.getAllCells().map((cell) => (
                    <td key={cell.id} {...stylex.props(styles.td)}>
                      <table.FlexRender cell={cell} />
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function Onboarding({
  node,
  connection,
  onClose,
}: {
  node: CloudNode;
  connection: CloudConnection | undefined;
  onClose: () => void;
}) {
  const cache = useQueryClient();
  const onboard = useMutation({
    mutationFn: cloudAPI.onboard,
    onSuccess: () => cache.invalidateQueries({ queryKey: ["cloud"] }),
  });
  const defaults: OnboardingForm = {
    alias: "",
    login: "",
    bootstrap_user: "ubuntu",
    bootstrap_address: node.public_ip || node.private_ip,
    port: "22",
    host_key: "",
    install_mode: "host",
    temporary_network: false,
  };
  const form = useForm({
    defaultValues: defaults,
    validators: { onSubmit: onboardingFormSchema },
    onSubmit: async ({ value }) => {
      await onboard.mutateAsync({ id: node.id, value });
      onClose();
    },
  });
  return (
    <section {...stylex.props(styles.panel)}>
      <h2 {...stylex.props(styles.panelHeading)}>Onboard {node.name}</h2>
      <p {...stylex.props(styles.description)}>
        The worker verifies the host, detects its OS and existing configuration, and selects a
        supported adapter. An incompatible NetBird installation remains unchanged and unmanaged.
      </p>
      <p {...stylex.props(styles.notice)}>
        Obtain the SSH host public key through a trusted administrative channel. Existing SSH
        recovery access, firewall rules and unrelated NetBird settings are preserved. Temporary
        bootstrap credentials and network access are removed after verification.
      </p>
      <form
        {...stylex.props(styles.formContainer)}
        onSubmit={(event) => {
          event.preventDefault();
          void form.handleSubmit();
        }}
      >
        <div {...stylex.props(styles.form)}>
          {(
            [
              ["alias", "Managed SSH alias"],
              ["login", "Managed login account"],
              ["bootstrap_user", "Existing administrator account"],
              ["bootstrap_address", "Bootstrap IP address"],
              ["port", "SSH port"],
              ["host_key", "Trusted SSH host public key"],
            ] as const
          ).map(([name, title]) => (
            <form.Field key={name} name={name}>
              {(field) => (
                <TextInput
                  label={title}
                  value={field.state.value}
                  onChange={field.handleChange}
                  isRequired
                  status={
                    field.state.meta.errors.length
                      ? { type: "error", message: "Check this value" }
                      : undefined
                  }
                />
              )}
            </form.Field>
          ))}
          <form.Field name="install_mode">
            {(field) => (
              <label {...stylex.props(styles.field)}>
                NetBird installation
                <select
                  {...stylex.props(styles.select)}
                  value={field.state.value}
                  onChange={(event) =>
                    field.handleChange(event.target.value === "docker" ? "docker" : "host")
                  }
                >
                  <option value="host">Directly on host (recommended)</option>
                  <option value="docker" disabled>
                    Docker (adapter validation pending)
                  </option>
                </select>
              </label>
            )}
          </form.Field>
          <form.Field name="temporary_network">
            {(field) => (
              <label>
                <input
                  type="checkbox"
                  checked={field.state.value}
                  disabled={!connection?.network_changes}
                  onChange={(event) => field.handleChange(event.target.checked)}
                />{" "}
                Allow a temporary worker-IP-only SSH rule
                {!connection?.network_changes && " (disabled for this connection)"}
              </label>
            )}
          </form.Field>
        </div>
        <form.Subscribe selector={(state) => state.errors.length > 0}>
          {(invalid) =>
            invalid && (
              <p role="alert">
                Check the alias, accounts, IP address, port and host key before onboarding.
              </p>
            )
          }
        </form.Subscribe>
        {onboard.error && (
          <p role="alert" {...stylex.props(styles.error)}>
            {errorMessage(onboard.error)}
          </p>
        )}
        <div {...stylex.props(styles.actions)}>
          <Button label="Start onboarding" type="submit" isLoading={onboard.isPending} />
          <Button label="Cancel" onClick={onClose} />
        </div>
      </form>
    </section>
  );
}

function Jobs({ state }: { state: CloudState }) {
  const cache = useQueryClient();
  const cancel = useMutation({
    mutationFn: cloudAPI.cancel,
    onSuccess: () => cache.invalidateQueries({ queryKey: ["cloud"] }),
  });
  const retry = useMutation({
    mutationFn: cloudAPI.retry,
    onSuccess: () => cache.invalidateQueries({ queryKey: ["cloud"] }),
  });
  return (
    <section {...stylex.props(styles.panel)}>
      <h2 {...stylex.props(styles.panelHeading)}>Onboarding and cleanup</h2>
      {(retry.error || cancel.error) && (
        <p role="alert" {...stylex.props(styles.error)}>
          {errorMessage(retry.error || cancel.error)}
        </p>
      )}
      {cancel.isSuccess && (
        <output>Cancellation requested. Temporary bootstrap access will be cleaned up.</output>
      )}
      {state.jobs.length === 0 && <p {...stylex.props(styles.empty)}>No onboarding jobs yet.</p>}
      {state.jobs.map((job) => (
        <div key={job.id} {...stylex.props(styles.pageHeading)}>
          <div>
            <strong>
              {state.nodes.find((node) => node.id === job.node_id)?.name ?? job.node_id}
            </strong>
            <p>
              <span {...stylex.props(styles.status)}>{label(job.status)}</span> {label(job.stage)}
            </p>
            <p {...stylex.props(styles.small)}>Updated {date(job.updated_at)}</p>
            {job.error && (
              <p role="alert" {...stylex.props(styles.error)}>
                {job.error}
              </p>
            )}
          </div>
          {["queued", "working"].includes(job.status) && (
            <Button
              label="Cancel onboarding"
              size="sm"
              isLoading={cancel.isPending && cancel.variables === job.id}
              onClick={() => cancel.mutate(job.id)}
            />
          )}
          {job.status === "cleanup_required" && (
            <Button
              label="Retry cleanup"
              size="sm"
              isLoading={retry.isPending && retry.variables === job.id}
              onClick={() => retry.mutate(job.id)}
            />
          )}
        </div>
      ))}
    </section>
  );
}

function UserAccess({ state }: { state: CloudState }) {
  const users = useQuery(cloudUsersQuery());
  const cache = useQueryClient();
  const managed = state.nodes.filter((node) => node.server_id);
  const assignment = useMutation({
    mutationFn: cloudAPI.assignUser,
    onSuccess: async () => {
      await cache.invalidateQueries({ queryKey: ["cloud-users"] });
      await cache.invalidateQueries({ queryKey: ["access"] });
    },
  });
  const form = useForm({
    defaultValues: { server_id: managed[0]?.server_id ?? "", subject: "" },
    onSubmit: async ({ value }) => {
      const user = users.data?.users.find((item) => item.subject === value.subject);
      if (
        !user ||
        user.subject === state.owner_subject ||
        !managed.some((node) => node.server_id === value.server_id)
      )
        return;
      await assignment.mutateAsync({
        serverID: value.server_id,
        value: { subject: user.subject, email: user.email, enabled: true },
      });
    },
  });
  const eligible = users.data?.users.filter((user) => user.subject !== state.owner_subject) ?? [];
  return (
    <section {...stylex.props(styles.panel)}>
      <h2 {...stylex.props(styles.panelHeading)}>Individual SSH access</h2>
      <p {...stylex.props(styles.description)}>
        Assign a signed-in user to a managed server. Changes become effective after successful
        access synchronization. Your owner access is inherited and cannot be revoked here. Manage
        group assignments in <Link to="/rules">Access rules</Link>.
      </p>
      {users.isPending && <p aria-busy="true">Loading verified users…</p>}
      {(users.error || assignment.error) && (
        <p role="alert" {...stylex.props(styles.error)}>
          {errorMessage(users.error || assignment.error)}
        </p>
      )}
      {users.data && eligible.length === 0 && (
        <p {...stylex.props(styles.notice)}>
          No other verified users are available. Ask the user to sign in first, then refresh this
          page to assign access.
        </p>
      )}
      {managed.length === 0 && (
        <p {...stylex.props(styles.empty)}>Onboard a server before assigning individual access.</p>
      )}
      {assignment.isSuccess && (
        <output>User assignment saved. Access synchronization applies the change.</output>
      )}
      {managed.length > 0 && eligible.length > 0 && (
        <form
          {...stylex.props(styles.formContainer)}
          onSubmit={(event) => {
            event.preventDefault();
            void form.handleSubmit();
          }}
        >
          <div {...stylex.props(styles.form)}>
            <form.Field name="server_id">
              {(field) => (
                <label {...stylex.props(styles.field)}>
                  Managed server
                  <select
                    {...stylex.props(styles.select)}
                    value={field.state.value}
                    required
                    onChange={(event) => field.handleChange(event.target.value)}
                  >
                    <option value="">Select a server</option>
                    {managed.map((node) => (
                      <option key={node.id} value={node.server_id}>
                        {node.name}
                      </option>
                    ))}
                  </select>
                </label>
              )}
            </form.Field>
            <form.Field name="subject">
              {(field) => (
                <label {...stylex.props(styles.field)}>
                  Verified user
                  <select
                    {...stylex.props(styles.select)}
                    value={field.state.value}
                    required
                    onChange={(event) => field.handleChange(event.target.value)}
                  >
                    <option value="">Select a user</option>
                    {eligible.map((user) => (
                      <option key={user.subject} value={user.subject}>
                        {user.name ? user.name + " · " : ""}
                        {user.email}
                      </option>
                    ))}
                  </select>
                </label>
              )}
            </form.Field>
          </div>
          <div {...stylex.props(styles.actions)}>
            <Button type="submit" label="Grant SSH access" isLoading={assignment.isPending} />
          </div>
        </form>
      )}
      {users.data && users.data.assignments.length > 0 && (
        <div {...stylex.props(styles.tableWrap)}>
          <table {...stylex.props(styles.table)}>
            <thead>
              <tr>
                <th {...stylex.props(styles.th)}>Server</th>
                <th {...stylex.props(styles.th)}>Identity</th>
                <th {...stylex.props(styles.th)}>Access</th>
                <th {...stylex.props(styles.th)}>Manage</th>
              </tr>
            </thead>
            <tbody>
              {users.data.assignments.map((item) => (
                <tr key={item.server_id + ":" + item.subject}>
                  <td {...stylex.props(styles.td)}>
                    {state.nodes.find((node) => node.server_id === item.server_id)?.name ??
                      item.server_id}
                  </td>
                  <td {...stylex.props(styles.td)}>{item.email}</td>
                  <td {...stylex.props(styles.td)}>
                    {item.subject === state.owner_subject
                      ? "Inherited owner access"
                      : item.enabled
                        ? "Assigned"
                        : "Revoked"}
                  </td>
                  <td {...stylex.props(styles.td)}>
                    {item.subject !== state.owner_subject && item.enabled && (
                      <Button
                        label={"Revoke access for " + item.email}
                        size="sm"
                        isLoading={
                          assignment.isPending &&
                          assignment.variables?.value.subject === item.subject &&
                          assignment.variables?.serverID === item.server_id
                        }
                        onClick={() =>
                          assignment.mutate({
                            serverID: item.server_id,
                            value: { subject: item.subject, email: item.email, enabled: false },
                          })
                        }
                      />
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
